import React, {
  useContext, useEffect, useRef, useMemo, useCallback,
} from 'react'
import {
  SelectedVehicle as CPSelectedVehicle,
  OverlayVehicleContext as OverlayVehicleContextProxy,
} from '@usap/widgets'
import { useResponsiveWrapperContext } from '@usap/uicomponents'
import { LocalContext } from '../../helpers/useLocalContext'
import urlDecode from '../../helpers/urlDecode'
import { useVehicleContext } from '../../contexts/vehicle.context'
import useVehicleController from '../../controllers/vehicle.controller'
import { isSEOPage } from '../../helpers/searchBoxFactory'
import {
  useVehicleImageContext,
  getFront,
  VEHICLE_IMAGE,
} from '../../contexts/vehicleImage.context'
import Container from './SelectedVehicle.css'
import validateAndUpdateVehicle from '../../helpers/validateAndUpdateVehicle'

const vehicleKeys = ['year', 'make', 'model']
const fullPathVehicle = ['year', 'make', 'model', 'submodel', 'engine']

const withValue = obj => obj && Object.keys(obj).length > 0 && obj

const getVehicle = (dynamicApiData, vehicleState) => {
  const products = dynamicApiData && (dynamicApiData.products || dynamicApiData.productDetails || {})
  const preselectedVehicle = withValue(products?.vehicle) || withValue(products?.selectedVehicle)

  // If we are on a page where we have products or productDetails
  // we always use those results, skipping global state
  if (products && preselectedVehicle) {
    return {
      vehicle: preselectedVehicle,
      products,
      isFromApiData: true,
    }
  }

  return { vehicle: vehicleState.selectedVehicle, products, isFromApiData: false }
}

// To create a label from vehicle selected
const createLabel = vehicleInstance => urlDecode(
  fullPathVehicle.reduce((label, key) => {
    const value = vehicleInstance[key] || ''
    return `${label} ${value}`
  }, ''),
  false,
  true,
)

const SelectedVehicle = ({
  dynamicApiData: details = {},
  history,
}) => {
  const vehicleState = useVehicleContext()
  const vehicleImages = useVehicleImageContext()
  const { updateVehicles } = useVehicleController()
  const initProductVehicleRef = useRef(true)
  const isPDPPage = details && details.pageType && details.pageType.includes('sku')

  const vehicleImageContext = useMemo(
    () => ({ vehicleImages, getFront, VEHICLE_IMAGE }),
    [vehicleImages],
  )
  const { vehicle, products, isFromApiData } = getVehicle(details, vehicleState)

  const selectedVehicleKeys = useMemo(
    () => (vehicle ? Object.keys(vehicle) : []),
    [vehicle],
  )

  // check length is greater than equal to 3
  const hasValidLength = selectedVehicleKeys.length >= 3

  // check has length greater than equal to 3 and and has vehicleKeys
  const hasValidKeys = useMemo(() => {
    if (!hasValidLength) return false
    const data = vehicleKeys.filter(key => selectedVehicleKeys.includes(key))
    return data.length === vehicleKeys.length
  }, [hasValidLength, selectedVehicleKeys])

  // Yes store in vehicle DB and show in UI
  const { localContext } = useContext(LocalContext)
  const { Link } = localContext

  const navigateToVLP = useCallback(() => {
    if (!vehicle || !Object.keys(vehicle).length) return ''
    const urlQuery = fullPathVehicle.reduce((query, param) => {
      const { [param]: paramValue } = vehicle
      if (!paramValue) {
        return query
      }
      return `${query}/${urlDecode(paramValue)}`
    }, '/vehicle')
    return history.push(urlQuery)
  }, [history, vehicle])

  // Get the vehicle from the products and update it in the vehicle DB
  useEffect(() => {
    // skip for pdp page as the vehicle locking is handled in loadData.js
    // before context initialization
    if (isPDPPage) return
    const firstLoad = __BROWSER__ && window.__FIRST_LOAD__
    if (firstLoad) {
      delete window.__FIRST_LOAD__
      return
    }
    if (!hasValidKeys) {
      return
    }
    if (
      isFromApiData &&
        initProductVehicleRef.current &&
        __BROWSER__ && !window.__FIRST_LOAD__
    ) {
      initProductVehicleRef.current = false

      const VehicleStorageCopy = vehicleState.vehicleDB || []
      const updatedDB = validateAndUpdateVehicle(
        VehicleStorageCopy,
        vehicle,
      )
      setTimeout(() => {
        updateVehicles({
          vehicleDB: updatedDB,
          showVehicleSelector: false,
          overlay: false,
        })
      })

      if (products && products.vehicleKeyword) {
        navigateToVLP(products.selectedVehicle)
      }
    }
  }, [
    products?.selectedVehicle,
    products?.searchKeyword,
  ])

  const changeVehicle = useCallback(() => {
    updateVehicles({
      showVehicleSelector: true,
      overlay: true,
      changeVehicleAction: true,
    })
  }, [updateVehicles])

  const { isDesktop } = useResponsiveWrapperContext()

  // Create a label to show in UI
  const vehicleLabel = useMemo(() => {
    if (!vehicle) return ''
    return createLabel(vehicle)
  }, [vehicle])

  if (products && products.isPla) return null
  if (!vehicle) return null
  if (!hasValidKeys) return null

  const pageType = (__BROWSER__ && window.pageType) || details.pageType

  if ((pageType === 'home' && isDesktop) ||
    pageType.includes('sku') ||
    isSEOPage(pageType, details)
  ) {
    return null
  }

  if (!vehicleState.selectedVehicle) return null

  return (
    <OverlayVehicleContextProxy.Provider
      value={vehicleImageContext}
    >
      <Container
        width="1248px"
      >
        <CPSelectedVehicle
          title={vehicleLabel}
          changeVehicle={changeVehicle}
          Link={Link}
          isDesktop={isDesktop}
          navigateToVLP={navigateToVLP}
          selectedVehicle={vehicle || null}
        />
      </Container>
    </OverlayVehicleContextProxy.Provider>
  )
}

export default SelectedVehicle
